import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, first, map, take, takeUntil } from 'rxjs/operators';

import {
  APP_USER_IDAM_REGISTER_ROUTE,
  APP_USER_LOGIN_ROUTE,
  APP_USER_REGISTER_ROUTE,
  APP_USER_VALIDATE_EMAIL_ROUTE,
  FeatureToggleEnum,
  NUMBER_1000,
  NUMBER_180,
  NUMBER_0,
  NUMBER_1,
} from '~shared/constants';
import { IAccountInformation } from '~shared/models/account-information.model';
import { PageService } from '~shared/services/page.service';
import { DOCUMENT } from '@angular/common';
import { environment } from '~env/environment';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { ButtonClickApplicationEvent, NavigationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationNavigationEventType } from '~shared/modules/application-event/constants';
import { MetroxTrackingLabels } from '~app/modules/generic/constants';
import { MobileMenuService } from '~app/modules/generic/services/mobile-menu.service';
import { MmPopoverRef, MmPopoverService } from '~shared/modules/popover/popover.module';
import { AccountDropdownComponent } from '../account-dropdown/account-dropdown.component';
import { MediaObserver } from '@angular/flex-layout';
import { AuthService } from '~app/modules/authorization/services/authorization-idam.service';
import { ShowElementSharedService } from '~app/modules/generic/services/show-element-shared-service';
import { AuthorizationIdamStateService } from '~app/modules/authorization/services/authorization-idam-state.service';
import {
  AuthorizationStateType,
  FF_FLYOUT_WEB_COMPONENT,
  FF_SL_CODE_LOGIN_DISABLE_SR,
  FF_SL_ENABLE_FALLBACK_FLYOUT_FOR_HEADQUATERS,
  FF_SL_REDIRECT_ANGULAR_SIGNUP_LOGIN_TO_REACT,
} from '~app/modules/authorization/constants/authorization.constant';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { debounce } from '~shared/decorators/debounce.decorator';
import { CONTEXT_CHANGE_DEBOUNCE } from '~app/modules/account/constants/flyout.constant';
import { AccountFlyoutService } from '~app/modules/account/services/account-flyout.service';
import { AccountFlyoutUtils } from '~app/modules/account/utils';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { GaTrackingEventCategory } from '~shared/modules/ga-tracking/constants';
import { AuthorizationState } from '~app/modules/authorization/models/authorization-state.model';
import { CookieScriptsUtil } from '~shared/modules/cookie-consent/utils/cookie-scripts.util';
import { METRO_APPS_FLYOUT_WEB_COMPONENT } from '~shared/modules/cookie-consent/constants/cookie-compass.constant';
import { isHeadQuarter, isMMCompanion, isOTPLoggedIn, isSoftLoggedIn } from '~server/utils/auth.utils';

@Component({
  selector: 'app-generic-user-box',
  templateUrl: './user-box.component.html',
  styleUrls: ['./user-box.component.scss'],
})
export class UserBoxComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() user: IAccountInformation;
  @Input() totalCartQuantity: string | number;
  @Output() userBoxClicked = new EventEmitter();
  @Input() isWideScreen: boolean;
  @Input() isMobileView: boolean;
  @ViewChild('userMenuMountPoint', { static: false }) userMenuMountPoint: ElementRef<HTMLElement>;
  popoverRef: MmPopoverRef;
  loginRoute = APP_USER_LOGIN_ROUTE;
  loginRouteQueryParams$: Observable<Object>;
  registerRoute = APP_USER_REGISTER_ROUTE;
  IDAMRegisterRoute = APP_USER_IDAM_REGISTER_ROUTE;
  iconButton = true;
  isUserMenuOpened = false;
  destroySubject = new Subject();
  isMobile = true;
  avatarIcon = `${environment.cdnBaseUrl()}/images/metrox/icon_avatar.svg`;
  private isNewOnRegisterEnabled = false;
  PendingState = AuthorizationStateType.Pending;
  GAAuthButtonClickEvent = 'AccessButtonClick';
  GAAuthButtonClickAction = 'global header click';
  GAAuthButtonClickEventRegLogin = 'register/login';
  GAAuthButtonClickEventLogin = 'login';
  GAAuthButtonClickEventReg = 'register';
  isExperimentEnabledForCombinedLoginSignup$: Observable<boolean>;

  authState$ = this.authIdamStateService.state$;
  flyoutPendingRefresh$ = this.accountFlyout.pendingForceRefresh$;
  private isOTPLoggedIn$ = this.activeFeaturesConfigcatService
    .isFeatureEnabledReadyForCurrentCountry$(FF_SL_CODE_LOGIN_DISABLE_SR)
    .pipe(map((on) => on && isOTPLoggedIn()));
  private isHeadQuarter$ = this.activeFeaturesConfigcatService
    .isFeatureEnabledReadyForCurrentCountry$(FF_SL_ENABLE_FALLBACK_FLYOUT_FOR_HEADQUATERS)
    .pipe(map((on) => on && isHeadQuarter()));
  isWebFlyoutEnabled$ = combineLatest([
    this.activeFeaturesConfigcatService.isFeatureEnabledReadyForCurrentCountry$(FF_FLYOUT_WEB_COMPONENT),
    this.isOTPLoggedIn$,
    this.isHeadQuarter$,
  ]).pipe(
    take(NUMBER_1),
    map(
      ([isWebFlyoutEnabled, isOtpLoginDone, isHQ]) =>
        isWebFlyoutEnabled && !isOtpLoginDone && !isSoftLoggedIn() && !isHQ && !isMMCompanion()
    )
  );
  unsubscribe$ = new Subject();
  shouldLoadFlyout$ = combineLatest([this.authState$, this.isWebFlyoutEnabled$]).pipe(
    filter(
      ([authState, isWebFlyoutEnabled]: [AuthorizationState, boolean]) =>
        authState.type === AuthorizationStateType.LoggedIn && isWebFlyoutEnabled && !UserBoxComponent.isScriptSetup
    ),
    take(NUMBER_1)
  );
  showUserBox$ = combineLatest([this.authState$, this.isOTPLoggedIn$]).pipe(
    filter(([authState, otpOn]) => authState.type !== AuthorizationStateType.Pending || otpOn),
    take(NUMBER_1)
  );
  public static isScriptSetup = false;
  private isLoginRedirectionToReactEnabled$ = this.activeFeaturesConfigcatService
    .isFeatureEnabledReadyForCurrentCountry$(FF_SL_REDIRECT_ANGULAR_SIGNUP_LOGIN_TO_REACT)
    .pipe(catchError(() => of(false)));

  constructor(
    private router: Router,
    private applicationEventService: ApplicationEventService,
    private pageService: PageService,
    private mobileMenuService: MobileMenuService,
    @Inject(DOCUMENT) private document: Document,
    private popoverSvc: MmPopoverService,
    private mediaObserver: MediaObserver,
    private authService: AuthService,
    private route: ActivatedRoute,
    private authIdamStateService: AuthorizationIdamStateService,
    private accountFlyout: AccountFlyoutService,
    private showElementSharedService: ShowElementSharedService,
    private accountInfoService: AccountInfoService,
    private activeFeaturesConfigcatService: ActiveFeaturesConfigcatService
  ) {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.isMobile =
          this.mediaObserver.isActive(MEDIA_ALISES.mobile) || this.mediaObserver.isActive(MEDIA_ALISES.tablet);
      });

    this.IDAMRegisterRoute = APP_USER_IDAM_REGISTER_ROUTE;
    this.activeFeaturesConfigcatService
      .isFeatureEnabled(FeatureToggleEnum.FF_NEW_ON_HEADER_REGISTER_CLICK)
      .pipe(first())
      .subscribe((on) => {
        this.isNewOnRegisterEnabled = on;
      });
  }

  openUserDropdown(origin: HTMLElement) {
    this.userBoxClicked.emit();
    this.showElementSharedService.showStickyAddToCartOnPdp(false);
    this.isUserMenuOpened = !this.isUserMenuOpened;
    this.isUserMenuOpened && this.isMobile
      ? this.document.body.classList.add('noscroll')
      : this.document.body.classList.remove('noscroll');
    if (this.isMobile) {
      this.closeNavigationMenu();
    } else {
      this.popoverRef = this.popoverSvc.open({
        origin,
        offsetY: 18,
        //164 max width of userbox + 16 half width of avatar icon
        offsetX: NUMBER_180 - this.userMenuMountPoint.nativeElement.getBoundingClientRect().width,
        content: AccountDropdownComponent,
        closeOnBackdropClick: true,
      });
    }
  }

  @HostListener('window:cms:user-profile:logout', ['$event'])
  handleLogout(e: Event) {
    e.preventDefault();
    this.authService.logout();
  }

  @HostListener('window:cms:user-profile:active-context-change', ['$event'])
  @debounce(CONTEXT_CHANGE_DEBOUNCE)
  handleAccountContextChange(e: any) {
    const eventContext = e?.activeContext || e?.detail;

    if (this.user?.activeContext && eventContext && this.user?.activeContext !== eventContext) {
      this.accountInfoService.fetchAccountInfo().pipe(take(1)).subscribe();
    }
  }

  public closeMobileMenu() {
    this.isUserMenuOpened = !this.isUserMenuOpened;
    this.document.body.classList.remove('noscroll');
  }

  ngOnInit(): void {
    this.loginRouteQueryParams$ = this.router.events.pipe(
      filter((event: RouterEvent) => event instanceof NavigationEnd),
      map(() => {
        const { backUrl } = this.pageService.getLoginUrlTree().queryParams;
        return backUrl && { backUrl };
      })
    );
    if (!this.isMobile) {
      this.router.events.pipe(takeUntil(this.destroySubject)).subscribe((routerEvent: RouterEvent) => {
        if (routerEvent instanceof NavigationStart && this.popoverRef) {
          this.popoverRef.close();
        }
      });
    }

    this.shouldLoadFlyout$.subscribe(() => {
      this.loadFlyoutScript();
    });
  }

  ngAfterViewInit(): void {
    this.subscribeForPendingFlyoutRefresh();
    if (this.route.snapshot.queryParams.refreshflyout) {
      setTimeout(() => AccountFlyoutUtils.refreshFlyout(), NUMBER_1000);
    }
  }

  subscribeForPendingFlyoutRefresh(): void {
    this.flyoutPendingRefresh$
      .pipe(
        filter((pending) => pending),
        takeUntil(this.destroySubject)
      )
      .subscribe(() => {
        AccountFlyoutUtils.refreshFlyout();
      });
  }

  handleLogin() {
    this.applicationEventService.publishIfFFEnabled(
      FeatureToggleEnum.FF_AUTH_BUTTONS_CLICK_GA_TRACKING,
      new ButtonClickApplicationEvent(
        this.GAAuthButtonClickEvent,
        GaTrackingEventCategory.Navigation.toLowerCase(),
        this.GAAuthButtonClickAction,
        this.GAAuthButtonClickEventRegLogin
      )
    );

    this.isLoginRedirectionToReactEnabled$.subscribe((on) => {
      if (on) {
        this.redirectToReactOTPModal();
      } else {
        this.redirectToValidateEmail();
      }
    });
  }

  private redirectToValidateEmail() {
    this.router.navigate([APP_USER_VALIDATE_EMAIL_ROUTE], {
      queryParams: { backUrl: this.router.url.split('?')[0] },
      queryParamsHandling: this.router.url.split('?')[NUMBER_0].endsWith(APP_USER_VALIDATE_EMAIL_ROUTE)
        ? 'preserve'
        : 'merge',
    });
  }

  private redirectToReactOTPModal(): void {
    const appBaseUrl = environment.appBaseUrl();

    const basePath = this.router.url.split('?')[0];

    const redirectUrl = new URL(appBaseUrl);
    redirectUrl.searchParams.set('backUrl', basePath);
    redirectUrl.searchParams.set('showOTPDialog', 'true');

    window.location.href = redirectUrl.toString();
  }

  redirectToRegistration() {
    this.applicationEventService.publishIfFFEnabled(
      FeatureToggleEnum.FF_AUTH_BUTTONS_CLICK_GA_TRACKING,
      new ButtonClickApplicationEvent(
        this.GAAuthButtonClickEvent,
        GaTrackingEventCategory.Navigation.toLowerCase(),
        this.GAAuthButtonClickAction,
        this.GAAuthButtonClickEventReg
      )
    );

    if (this.isNewOnRegisterEnabled) {
      const currentRoute = this.router.url.split('?')[0];

      if (currentRoute !== `/${APP_USER_IDAM_REGISTER_ROUTE}`) {
        this.router.navigate([APP_USER_IDAM_REGISTER_ROUTE], {
          queryParams: { backUrl: this.router.url },
        });
      }
    } else {
      this.router.navigate([APP_USER_IDAM_REGISTER_ROUTE], {
        queryParams: { backUrl: this.router.url },
      });
    }
  }

  handleUserBoxAction() {
    this.userBoxClicked.emit();
    this.applicationEventService.publish(
      new NavigationApplicationEvent(ApplicationNavigationEventType.GlobalHeaderClick, MetroxTrackingLabels.Login)
    );
    if (this.isMobile) {
      this.closeNavigationMenu();
    }
  }

  trackSignUpAction() {
    this.applicationEventService.publish(
      new NavigationApplicationEvent(ApplicationNavigationEventType.GlobalHeaderClick, MetroxTrackingLabels.SignUp)
    );
  }

  closeNavigationMenu() {
    this.mobileMenuService.close();
  }

  loadFlyoutScript() {
    UserBoxComponent.isScriptSetup = true;
    CookieScriptsUtil.createHeadScript(this.document, METRO_APPS_FLYOUT_WEB_COMPONENT(), 'flyoutScript').catch(
      () => {}
    );
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
  }
}
